import { useState, useRef, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Drawer from '../Common/Drawer';
import { InputFormik, SearchableDropdown, Button, Dropdown } from '../Common';
import { ProjectRoleDto, ProjectAssigneesWithUserInfoDto } from '../../commonTypes/project.types';

interface IMemberAssignment {
    memberId: string;
    role: string | number;
    billableType: string;
    employmentType: string;
    rate: string;
}

interface IAssignMembersDrawerProps {
    open: boolean;
    onClose: () => void;
    onAssign: (members: IMemberAssignment[]) => void;
    availableMembers: { label: string; value: string; imageUrl?: string; jobTitle?: string }[];
    employmentTypes: { label: string; value: string }[];
    projectRoles: ProjectRoleDto[];
    editMode?: boolean;
    editMember?: ProjectAssigneesWithUserInfoDto;
}

const billableOptions = [
    { label: 'Billable', value: 'billable' },
    { label: 'Non-billable', value: 'non-billable' },
];

const validationSchema = Yup.object().shape({
    members: Yup.array().of(
        Yup.object().shape({
            memberId: Yup.string().required('Member is required'),
            role: Yup.string().required('Role is required'),
            billableType: Yup.string().required('Billable type is required'),
            employmentType: Yup.string().when('billableType', {
                is: 'billable',
                then: schema => schema.required('Employment type is required'),
                otherwise: schema => schema,
            }),
            rate: Yup.string().when('billableType', {
                is: 'billable',
                then: schema => schema.required('Rate is required'),
                otherwise: schema => schema,
            }),
        })
    ),
});

const AssignMembersDrawer = ({
    open,
    onClose,
    onAssign,
    availableMembers,
    employmentTypes,
    projectRoles,
    editMode = false,
    editMember,
}: IAssignMembersDrawerProps) => {
    const formatRoleName = (name: string) => {
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    };

    const roleOptions = projectRoles.map(role => ({ label: formatRoleName(role.name), value: role.id }));

    const getInitialMemberAssignments = (): IMemberAssignment[] => {
        if (editMode && editMember) {
            const isBillable = editMember.rate != null && editMember.rate > 0;
            return [
                {
                    memberId: editMember.userSub,
                    role: editMember.assignedRoles?.[0]?.projectRoleDto?.id?.toString() || '',
                    billableType: isBillable ? 'billable' : 'non-billable',
                    employmentType: (editMember as any).employmentType || 'Full-time',
                    rate: editMember.rate?.toString() || '',
                },
            ];
        }
        return [
            {
                memberId: '',
                role: '',
                billableType: 'billable',
                employmentType: 'Full-time',
                rate: '',
            },
        ];
    };

    const [memberAssignments, setMemberAssignments] = useState<IMemberAssignment[]>(getInitialMemberAssignments());
    const lastFormRef = useRef<HTMLDivElement>(null);

    const findRole = (roleId: string | number) => {
        return roleOptions.find(role => role.value === roleId) || { label: '', value: '' };
    };

    const findMember = (memberId: string) => {
        return availableMembers.find(member => member.value === memberId) || { label: '', value: '' };
    };

    const findEmploymentType = (typeId: string) => {
        return employmentTypes.find(type => type.value === typeId) || { label: '', value: '' };
    };

    const findBillableType = (billableTypeId: string) => {
        return billableOptions.find(type => type.value === billableTypeId) || { label: '', value: '' };
    };

    const context = useFormik({
        initialValues: { members: memberAssignments },
        validationSchema,
        enableReinitialize: true,
        onSubmit: values => {
            onAssign(values.members);
            handleClose();
        },
    });

    useEffect(() => {
        if (lastFormRef.current && memberAssignments.length > 1) {
            lastFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [memberAssignments.length]);

    useEffect(() => {
        if (open) {
            setMemberAssignments(getInitialMemberAssignments());
        }
    }, [open, editMode, editMember]);

    const handleClose = () => {
        setMemberAssignments([
            {
                memberId: '',
                role: '',
                billableType: 'billable',
                employmentType: '',
                rate: '',
            },
        ]);
        context.resetForm();
        onClose();
    };

    const handleAddMoreMembers = () => {
        setMemberAssignments([
            ...memberAssignments,
            {
                memberId: '',
                role: '',
                billableType: 'billable',
                employmentType: '',
                rate: '',
            },
        ]);
    };

    const handleRemoveMember = (index: number) => {
        const updatedMembers = memberAssignments.filter((_, i) => i !== index);
        setMemberAssignments(updatedMembers);
    };

    const updateMemberField = (index: number, field: keyof IMemberAssignment, value: string) => {
        const updatedMembers = [...memberAssignments];
        updatedMembers[index][field] = value;

        if (field === 'billableType' && value === 'non-billable') {
            updatedMembers[index].employmentType = 'Full-time';
            updatedMembers[index].rate = '';
        }

        setMemberAssignments(updatedMembers);
        context.setFieldValue('members', updatedMembers);
    };

    const getAvailableMembersForIndex = (currentIndex: number) => {
        const selectedMemberIds = memberAssignments
            .map((assignment, index) => (index !== currentIndex ? assignment.memberId : null))
            .filter(id => id !== null && id !== '');

        return availableMembers.filter(member => !selectedMemberIds.includes(member.value));
    };

    const isFormValid = () => {
        return memberAssignments.every(member => {
            const baseValid = member.memberId && member.role && member.billableType;
            if (member.billableType === 'non-billable') {
                return baseValid;
            }
            return baseValid && member.employmentType && member.rate.trim();
        });
    };

    return (
        <Drawer
            open={open}
            title={editMode ? 'Edit assignee' : 'Assign Memeber'}
            onClose={handleClose}
            showFooter={true}
            className="assign-members-drawer"
            rightButtons={[
                {
                    text: 'Cancel',
                    variant: 'text',
                    onClick: handleClose,
                },
                {
                    text: editMode ? 'Save' : 'Assign Member',
                    variant: 'contained',
                    onClick: () => context.handleSubmit(),
                    disabled: !isFormValid(),
                },
            ]}
        >
            <div className="assign-members-content">
                {editMode && editMember && (
                    <div className="edit-member__info">
                        <img
                            src={editMember.imageUrl || '/svg/person.svg'}
                            alt={`${editMember.givenName} ${editMember.familyName}`}
                            className="edit-member__avatar"
                            onError={e => ((e.target as HTMLImageElement).src = '/svg/person.svg')}
                        />
                        <div className="edit-member__name">
                            {`${editMember.givenName || ''} ${editMember.familyName || ''}`.trim() || 'Unknown Member'}
                        </div>
                    </div>
                )}
                {memberAssignments.map((member, index) => (
                    <div key={index} className="member-assignment-form" ref={index === memberAssignments.length - 1 ? lastFormRef : null}>
                        {!editMode && (
                            <SearchableDropdown
                                label="Select Member"
                                menuItems={getAvailableMembersForIndex(index)}
                                selected={findMember(member.memberId)}
                                onChange={item => updateMemberField(index, 'memberId', item.value as string)}
                                isRequired
                                showImage
                            />
                        )}
                        {!editMode && (
                            <Dropdown
                                label="Role"
                                menuItems={roleOptions}
                                selected={findRole(member.role)}
                                onChange={item => updateMemberField(index, 'role', item.value as string)}
                            />
                        )}
                        <Dropdown
                            label="Billable Type"
                            menuItems={billableOptions}
                            selected={findBillableType(member.billableType)}
                            onChange={item => updateMemberField(index, 'billableType', item.value as string)}
                        />

                        <SearchableDropdown
                            label="Select Employment Type"
                            menuItems={employmentTypes}
                            selected={findEmploymentType(member.employmentType)}
                            onChange={item => updateMemberField(index, 'employmentType', item.value as string)}
                            isRequired
                            disabled={member.billableType === 'non-billable'}
                        />

                        <InputFormik
                            context={context}
                            id={`members.${index}.rate`}
                            label="Rate"
                            placeholder="Enter Hourly Rate"
                            value={member.rate}
                            onChange={e => updateMemberField(index, 'rate', e.target.value)}
                            isRequired
                            isDisabled={member.billableType === 'non-billable'}
                        />

                        {memberAssignments.length > 1 && (
                            <button type="button" className="remove-member-btn" onClick={() => handleRemoveMember(index)}>
                                X
                            </button>
                        )}
                    </div>
                ))}

                {!editMode && (
                    <Button text="Add More Members" variant="text" iconSrc="/svg/plus-blue-without-circle.svg" onClick={handleAddMoreMembers} />
                )}
            </div>
        </Drawer>
    );
};

export default AssignMembersDrawer;
